<template>
  <el-card v-loading="loading" shadow="never" :class="{ 'tech-card': tech }">
    <template #header>
      <div class="flex flex-row flex-wrap items-center justify-between gap-3">
        <CardTitle
          :title="
            shopCtx.tradeScopeHint
              ? `交易量趋势（${shopCtx.tradeScopeHint}）`
              : '交易量趋势'
          "
        />
        <el-radio-group v-model="timeRangeType" size="small" @change="handleTimeRangeTypeChange">
          <el-radio-button
            v-for="[key, value] in timeRangeMeta.entries()"
            :key="key"
            :value="key"
          >
            {{ value.name }}
          </el-radio-button>
        </el-radio-group>
      </div>
    </template>

    <div class="chart-block">
      <div class="chart-title">支付金额（元）</div>
      <Echart :height="chartHeight" :options="priceChartOptions" />
    </div>
    <div class="chart-block">
      <div class="chart-title">订单笔数（笔）</div>
      <Echart :height="chartHeight" :options="countChartOptions" />
    </div>
  </el-card>
</template>

<script lang="ts" setup>
import { computed, onMounted, reactive, ref, watch, withDefaults } from 'vue'
import dayjs, { Dayjs } from 'dayjs'
import type { EChartsOption } from 'echarts'
import * as TradeStatisticsApi from '@/api/mall/statistics/trade'
import type { DataComparisonRespVO } from '@/api/mall/statistics/common'
import type { TradeOrderTrendRespVO } from '@/api/mall/statistics/trade'
import { fenToYuanNumber } from '@/utils'
import { CardTitle } from '@/components/Card'
import { useMallSelfShopContext } from '@/hooks/web/useMallSelfShopContext'

/**
 * 交易量趋势（纯 ECharts）
 * - 金额 / 订单分两张图，避免同轴混读
 * - 周 / 月 / 年补齐完整横轴，并展示本期+对照期具体日期
 */
defineOptions({ name: 'TradeTrendCard' })

const props = withDefaults(
  defineProps<{
    tech?: boolean
  }>(),
  { tech: false }
)

enum TimeRangeTypeEnum {
  DAY30 = 1,
  WEEK = 7,
  MONTH = 30,
  YEAR = 365
}

interface TrendRow {
  /** 本期日期（或月份锚点） */
  date: string
  /** 对照期日期 */
  referenceDate: string
  /** 横轴展示文案（支持换行） */
  label: string
  currentPrice: number
  referencePrice: number
  currentCount: number
  referenceCount: number
}

const WEEKDAY = ['日', '一', '二', '三', '四', '五', '六']

const { context: shopCtx, buildShopFilter, refresh: refreshShopCtx } = useMallSelfShopContext()

const timeRangeType = ref(TimeRangeTypeEnum.DAY30)
const loading = ref(true)
const trendRows = ref<TrendRow[]>([])

const timeRangeMeta = new Map([
  [TimeRangeTypeEnum.DAY30, { name: '30天' }],
  [TimeRangeTypeEnum.WEEK, { name: '周' }],
  [TimeRangeTypeEnum.MONTH, { name: '月' }],
  [TimeRangeTypeEnum.YEAR, { name: '年' }]
])

const isCompareMode = computed(() => timeRangeType.value !== TimeRangeTypeEnum.DAY30)

const periodLabels = computed(() => {
  switch (timeRangeType.value) {
    case TimeRangeTypeEnum.WEEK:
      return { current: '本周', reference: '上周' }
    case TimeRangeTypeEnum.MONTH:
      return { current: '本月', reference: '上月' }
    case TimeRangeTypeEnum.YEAR:
      return { current: '今年', reference: '去年' }
    default:
      return { current: '本期', reference: '上期' }
  }
})

/** 周/月标签行数多，图要高一点；年/30天次之 */
const chartHeight = computed(() => {
  switch (timeRangeType.value) {
    case TimeRangeTypeEnum.WEEK:
    case TimeRangeTypeEnum.MONTH:
      return 300
    case TimeRangeTypeEnum.YEAR:
      return 280
    default:
      return 260
  }
})

const priceChartOptions = reactive<EChartsOption>(createBaseChartOptions()) as EChartsOption
const countChartOptions = reactive<EChartsOption>(createBaseChartOptions()) as EChartsOption

function createBaseChartOptions(): EChartsOption {
  return {
    color: ['#94a3b8', '#5b8ff9'],
    grid: { left: 12, right: 16, bottom: 8, top: 40, containLabel: true },
    legend: { top: 4, left: 'center', data: [] },
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    xAxis: {
      type: 'category',
      boundaryGap: true,
      axisTick: { alignWithLabel: true },
      axisLabel: {
        interval: 0,
        hideOverlap: true,
        lineHeight: 16,
        formatter: (value: string) => value
      },
      data: []
    },
    yAxis: {
      type: 'value',
      minInterval: 1,
      axisTick: { show: false },
      splitLine: { lineStyle: { type: 'dashed', color: '#e8e8e8' } }
    },
    series: []
  }
}

/** 空桶：接口只回有成交的天，前端按完整周期补齐，横轴才完整 */
function buildEmptyBuckets(begin: Dayjs, end: Dayjs): TrendRow[] {
  const rows: TrendRow[] = []
  const type = timeRangeType.value

  if (type === TimeRangeTypeEnum.YEAR) {
    let cursor = begin.startOf('month')
    const last = end.startOf('month')
    while (cursor.isBefore(last) || cursor.isSame(last, 'month')) {
      const ref = cursor.subtract(1, 'year')
      rows.push(createEmptyRow(cursor.format('YYYY-MM-DD'), ref.format('YYYY-MM-DD')))
      cursor = cursor.add(1, 'month')
    }
    return rows
  }

  let cursor = begin.startOf('day')
  const last = end.startOf('day')
  while (cursor.isBefore(last) || cursor.isSame(last, 'day')) {
    // 按日历对齐：上周同星期 / 上月同日，而不是整段天数平移
    let ref = cursor
    if (type === TimeRangeTypeEnum.WEEK) {
      ref = cursor.subtract(1, 'week')
    } else if (type === TimeRangeTypeEnum.MONTH) {
      ref = cursor.subtract(1, 'month')
    } else {
      // 30 天模式无对照列，占位即可
      ref = cursor.subtract(30, 'day')
    }
    rows.push(createEmptyRow(cursor.format('YYYY-MM-DD'), ref.format('YYYY-MM-DD')))
    cursor = cursor.add(1, 'day')
  }
  return rows
}

function createEmptyRow(date: string, referenceDate: string): TrendRow {
  return {
    date,
    referenceDate,
    label: '',
    currentPrice: 0,
    referencePrice: 0,
    currentCount: 0,
    referenceCount: 0
  }
}

function periodKey(date?: string) {
  if (!date) return ''
  return timeRangeType.value === TimeRangeTypeEnum.YEAR
    ? dayjs(date).format('YYYY-MM')
    : dayjs(date).format('YYYY-MM-DD')
}

/** 对照期完整日历区间（周/月/年） */
function getReferenceRange(begin: Dayjs, end: Dayjs): [Dayjs, Dayjs] {
  switch (timeRangeType.value) {
    case TimeRangeTypeEnum.WEEK:
      return [begin.subtract(1, 'week'), end.subtract(1, 'week')]
    case TimeRangeTypeEnum.MONTH:
      return [
        begin.subtract(1, 'month').startOf('month'),
        begin.subtract(1, 'month').endOf('month')
      ]
    case TimeRangeTypeEnum.YEAR:
      return [
        begin.subtract(1, 'year').startOf('year'),
        begin.subtract(1, 'year').endOf('year')
      ]
    default:
      return [begin, end]
  }
}

/** 只取接口里的本期 value（对照期另起一次请求，避免后端按下标截断） */
function collectValuePoints(list: DataComparisonRespVO<TradeOrderTrendRespVO>[]) {
  const map = new Map<string, TradeOrderTrendRespVO>()
  for (const item of list) {
    if (item.value?.date) {
      map.set(periodKey(item.value.date), item.value)
    }
  }
  return map
}

function applyPointsToBuckets(
  buckets: TrendRow[],
  currentMap: Map<string, TradeOrderTrendRespVO>,
  referenceMap: Map<string, TradeOrderTrendRespVO>
) {
  for (const row of buckets) {
    const cur = currentMap.get(periodKey(row.date))
    const ref = referenceMap.get(periodKey(row.referenceDate))
    if (cur) {
      row.currentPrice = fenToYuanNumber(cur.orderPayPrice || 0)
      row.currentCount = cur.orderPayCount || 0
    }
    if (ref) {
      row.referencePrice = fenToYuanNumber(ref.orderPayPrice || 0)
      row.referenceCount = ref.orderPayCount || 0
    }
  }
}

/**
 * 横轴文案（换行）：
 * - 周：周三 / 本 09-03 / 上 08-27
 * - 月：本 09-03 / 上 08-03
 * - 年：1月 / 2026 / 2025
 * - 30天：09-03
 */
function formatAxisLabel(row: TrendRow): string {
  const cur = dayjs(row.date)
  const ref = dayjs(row.referenceDate)
  switch (timeRangeType.value) {
    case TimeRangeTypeEnum.WEEK:
      return [`周${WEEKDAY[cur.day()]}`, `本 ${cur.format('MM-DD')}`, `上 ${ref.format('MM-DD')}`].join(
        '\n'
      )
    case TimeRangeTypeEnum.MONTH:
      return [`本 ${cur.format('MM-DD')}`, `上 ${ref.format('MM-DD')}`].join('\n')
    case TimeRangeTypeEnum.YEAR:
      return [`${cur.month() + 1}月`, `${cur.year()}`, `${ref.year()}`].join('\n')
    case TimeRangeTypeEnum.DAY30:
    default:
      return cur.format('MM-DD')
  }
}

function buildTooltip(row: TrendRow) {
  const { current, reference } = periodLabels.value
  const curDate =
    timeRangeType.value === TimeRangeTypeEnum.YEAR
      ? dayjs(row.date).format('YYYY年M月')
      : dayjs(row.date).format('YYYY-MM-DD')
  const refDate =
    timeRangeType.value === TimeRangeTypeEnum.YEAR
      ? dayjs(row.referenceDate).format('YYYY年M月')
      : dayjs(row.referenceDate).format('YYYY-MM-DD')

  let html = `<div style="font-weight:600;margin-bottom:6px">${curDate}</div>`
  html += `<div>${current}金额：¥${row.currentPrice.toFixed(2)}</div>`
  html += `<div>${current}订单：${row.currentCount} 笔</div>`
  if (isCompareMode.value) {
    html += `<div style="margin-top:6px;color:#909399">对照 ${refDate}</div>`
    html += `<div>${reference}金额：¥${row.referencePrice.toFixed(2)}</div>`
    html += `<div>${reference}订单：${row.referenceCount} 笔</div>`
  }
  return html
}

function applyAxisStyle(options: EChartsOption) {
  const type = timeRangeType.value
  const bottom = type === TimeRangeTypeEnum.DAY30 ? 8 : 20
  const rotate = type === TimeRangeTypeEnum.MONTH ? 45 : type === TimeRangeTypeEnum.DAY30 ? 40 : 0
  const fontSize = type === TimeRangeTypeEnum.WEEK || type === TimeRangeTypeEnum.YEAR ? 11 : 10

  options.grid = {
    left: 12,
    right: 16,
    bottom,
    top: 40,
    containLabel: true
  }
  ;(options.xAxis as any).axisLabel = {
    interval: type === TimeRangeTypeEnum.MONTH ? 'auto' : 0,
    hideOverlap: true,
    rotate,
    lineHeight: 15,
    fontSize,
    formatter: (value: string) => value
  }
}

function applyTechTheme(options: EChartsOption) {
  if (!props.tech) {
    options.color = ['#94a3b8', '#5b8ff9']
    ;(options.yAxis as any).splitLine = { lineStyle: { type: 'dashed', color: '#e8e8e8' } }
    return
  }
  options.color = ['#6f8fb3', '#00f2fe']
  options.textStyle = { color: '#9fc4ee' }
  ;(options.legend as any).textStyle = { color: '#9fc4ee' }
  ;(options.xAxis as any).axisLabel = {
    ...(options.xAxis as any).axisLabel,
    color: '#6f8fb3'
  }
  ;(options.yAxis as any).axisLabel = { color: '#6f8fb3' }
  ;(options.yAxis as any).splitLine = {
    lineStyle: { type: 'dashed', color: 'rgba(28,156,255,0.15)' }
  }
}

function rebuildCharts() {
  const rows = trendRows.value
  const labels = rows.map((r) => r.label)
  const { current, reference } = periodLabels.value

  const fillChart = (
    options: EChartsOption,
    metric: 'price' | 'count',
    yName: string
  ) => {
    const currentData = rows.map((r) => (metric === 'price' ? r.currentPrice : r.currentCount))
    const referenceData = rows.map((r) =>
      metric === 'price' ? r.referencePrice : r.referenceCount
    )

    applyAxisStyle(options)
    ;(options.xAxis as any).data = labels
    ;(options.yAxis as any).name = yName
    ;(options.tooltip as any).formatter = (params: any[]) => {
      if (!params?.length) return ''
      const row = rows[params[0].dataIndex]
      return row ? buildTooltip(row) : ''
    }

    if (isCompareMode.value) {
      const names = [
        `${reference}${metric === 'price' ? '金额' : '订单'}`,
        `${current}${metric === 'price' ? '金额' : '订单'}`
      ]
      ;(options.legend as any).data = names
      options.series = [
        {
          name: names[0],
          type: 'bar',
          barMaxWidth: 22,
          barGap: '20%',
          data: referenceData,
          itemStyle: { borderRadius: [3, 3, 0, 0] }
        },
        {
          name: names[1],
          type: 'bar',
          barMaxWidth: 22,
          data: currentData,
          itemStyle: { borderRadius: [3, 3, 0, 0] }
        }
      ]
    } else {
      const name = metric === 'price' ? '订单金额' : '订单数量'
      ;(options.legend as any).data = [name]
      options.series = [
        {
          name,
          type: 'bar',
          barMaxWidth: 18,
          data: currentData,
          itemStyle: { borderRadius: [3, 3, 0, 0], color: '#5b8ff9' }
        }
      ]
    }
    applyTechTheme(options)
  }

  fillChart(priceChartOptions, 'price', '金额（元）')
  fillChart(countChartOptions, 'count', '订单（笔）')
}

async function handleTimeRangeTypeChange() {
  let beginTime: Dayjs
  let endTime: Dayjs
  switch (timeRangeType.value) {
    case TimeRangeTypeEnum.WEEK:
      beginTime = dayjs().startOf('week')
      endTime = dayjs().endOf('week')
      break
    case TimeRangeTypeEnum.MONTH:
      beginTime = dayjs().startOf('month')
      endTime = dayjs().endOf('month')
      break
    case TimeRangeTypeEnum.YEAR:
      beginTime = dayjs().startOf('year')
      endTime = dayjs().endOf('year')
      break
    case TimeRangeTypeEnum.DAY30:
    default:
      beginTime = dayjs().subtract(30, 'day').startOf('d')
      endTime = dayjs().endOf('d')
      break
  }
  await loadTrend(beginTime, endTime)
}

async function loadTrend(beginTime: Dayjs, endTime: Dayjs) {
  loading.value = true
  try {
    await refreshShopCtx()
    const shopFilter = buildShopFilter()
    const type = timeRangeType.value

    // 后端对照列表长度被「本期有成交的天数」截断，上月成交日更多时会被丢掉。
    // 对比模式改为再请求一次完整对照期，两边都取 value，再按日期对齐。
    const currentPromise = TradeStatisticsApi.getOrderCountTrendComparison(
      type,
      beginTime,
      endTime,
      shopFilter
    )
    const referencePromise = isCompareMode.value
      ? (() => {
          const [refBegin, refEnd] = getReferenceRange(beginTime, endTime)
          return TradeStatisticsApi.getOrderCountTrendComparison(
            type,
            refBegin,
            refEnd,
            shopFilter
          )
        })()
      : Promise.resolve([] as DataComparisonRespVO<TradeOrderTrendRespVO>[])

    const [currentList, referenceList] = await Promise.all([currentPromise, referencePromise])

    const buckets = buildEmptyBuckets(beginTime, endTime)
    applyPointsToBuckets(
      buckets,
      collectValuePoints(currentList || []),
      collectValuePoints(referenceList || [])
    )
    for (const row of buckets) {
      row.label = formatAxisLabel(row)
    }
    trendRows.value = buckets
    rebuildCharts()
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void handleTimeRangeTypeChange()
})

watch(
  () => shopCtx.value.selfScope,
  () => {
    void handleTimeRangeTypeChange()
  }
)
</script>

<style scoped lang="scss">
.chart-block {
  & + & {
    margin-top: 8px;
    padding-top: 12px;
    border-top: 1px dashed var(--el-border-color-lighter);
  }
}

.chart-title {
  margin-bottom: 4px;
  font-size: 13px;
  font-weight: 600;
  color: var(--el-text-color-regular);
}

.tech-card {
  color: #e6f7ff;
  background: transparent;
  border: none;

  :deep(.el-card__header) {
    border-bottom-color: rgba(0, 242, 254, 0.15);
  }

  :deep(.el-radio-button__inner) {
    color: #9fc4ee;
    background: rgba(15, 40, 72, 0.8);
    border-color: rgba(28, 156, 255, 0.35);
  }

  :deep(.el-radio-button__original-radio:checked + .el-radio-button__inner) {
    color: #031525;
    background: linear-gradient(135deg, #00f2fe, #1c9cff);
    border-color: transparent;
    box-shadow: none;
  }

  .chart-block + .chart-block {
    border-top-color: rgba(28, 156, 255, 0.2);
  }

  .chart-title {
    color: #9fc4ee;
  }
}
</style>
